import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import * as brokerHooks from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import * as paymentActions from "@rentiohq/shared-frontend/dist/redux/payment/payment.actions";
import * as contractHooks from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.hooks";
import * as paymentOrderActions from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.actions";
import * as paymentOrderHooks from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.hooks";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import {
  EPaymentOrderRole,
  EPaymentOrderType,
  EPaymentRepetitionType,
  IPaymentOrder,
} from "@rentiohq/shared-frontend/dist/types/payment.types";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { isSameDay } from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { getTitle } from "@rentiohq/shared-frontend/dist/utils/paymentOrder.utils";
import {
  Button,
  Grid,
  Icon,
  Page,
  RentioInternalRenderer,
  Stack,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import { IAction } from "@rentiohq/web-shared/dist/types";
import utils from "@rentiohq/web-shared/dist/utils";
import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAmounts } from "../../../../scenes/Properties/scenes/Payments/scenes/PaymentsOverview";
import { PaymentOrderHistory } from "./components/PaymentOrderHistory";
import { PaymentOrderInfo } from "./components/PaymentOrderInfo";
import {
  EPaymentOrderTabs,
  IPaymentOrderContainerProps,
} from "./PaymentOrder.types";

export const PaymentOrder = (props: IPaymentOrderContainerProps) => {
  const { paymentOrderId } = props;

  const { detail: paymentOrder } = paymentOrderHooks.useDetail({
    shouldRefetch: false,
    id: paymentOrderId,
  });

  const { detail: contract } = contractHooks.useDetail({
    shouldRefetch: false,
    id: paymentOrder?.contractId,
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tab, setTab] = React.useState<EPaymentOrderTabs>(
    EPaymentOrderTabs.Info,
  );

  const hasBookkeeping = brokerHooks.useBrokerFeature(
    EBrokerFeature.Bookkeeping,
  );

  React.useEffect(() => {
    dispatch(
      paymentOrderActions.getDetailStart.getAction({ id: paymentOrderId }),
    );
  }, [paymentOrderId]);

  if (!paymentOrder) {
    return null;
  }

  const amounts = getAmounts(paymentOrder);

  const startDate = paymentOrder.startedAt
    ? utils.date.format(paymentOrder.startedAt, "d MMMM yyyy")
    : undefined;
  const endDate = paymentOrder.endedAt
    ? utils.date.format(paymentOrder.endedAt, "d MMMM yyyy")
    : undefined;

  const contractStartDate = contract?.startDate
    ? utils.date.format(contract.startDate, "d MMMM yyyy")
    : undefined;
  const contractEndDate = contract?.stopDate
    ? utils.date.format(contract.stopDate, "d MMMM yyyy")
    : undefined;

  const period =
    startDate &&
    endDate &&
    paymentOrder.endedAt &&
    !isSameDay(paymentOrder.startedAt, paymentOrder.endedAt)
      ? [startDate, endDate].join(" - ")
      : undefined;

  const metadata = (
    <div>
      <RentioInternalRenderer>V2</RentioInternalRenderer>

      <Spacer weight={ESpacerWeight.W08} />

      {paymentOrder.propertyTypeId && (
        <Stack.Item>
          <Grid spacing="extraTight" alignItems="center">
            <Grid.Item>
              <Icon
                source={utils.properties.getIcon(
                  paymentOrder.propertyTypeId || 0,
                )}
                size="small"
              />
            </Grid.Item>
            <Grid.Item>
              <Button
                appearance="link"
                url={`/properties/${paymentOrder.propertyId}`}
              >
                {paymentOrder.propertyAddress?.full}
              </Button>
            </Grid.Item>
          </Grid>
        </Stack.Item>
      )}

      {amounts.vat > 0 && (
        <>
          <Spacer weight={ESpacerWeight.W08} />
          <Stack.Item>
            <Grid spacing="extraTight" alignItems="center">
              <Grid.Item>
                <Icon source="billStack" size="small" />
              </Grid.Item>
              <Grid.Item>
                {getLocalizedText("system.amount_with_vat", {
                  amount: formatCurrency(amounts.amount),
                  vat: formatCurrency(amounts.amountVat),
                })}
              </Grid.Item>
            </Grid>
          </Stack.Item>
        </>
      )}

      <Spacer weight={ESpacerWeight.W08} />
      <Stack.Item>
        <Grid spacing="extraTight" alignItems="center">
          <Grid.Item>
            <Icon source="leaseContract" size="small" />
          </Grid.Item>
          <Grid.Item>
            {contractStartDate && contractEndDate
              ? getLocalizedText("payment_order.contract.contract_duration", {
                  startDate: contractStartDate,
                  endDate: contractEndDate,
                })
              : getLocalizedText("payment_order.contract.no_contract")}
          </Grid.Item>
        </Grid>
      </Stack.Item>

      {paymentOrder.type === EPaymentOrderType.RentDeposit &&
        paymentOrder.wireReference && (
          <>
            <Spacer weight={ESpacerWeight.W08} />
            <Stack.Item>
              <Grid spacing="extraTight" alignItems="center">
                <Grid.Item>
                  <Icon source="billStack" size="small" />
                </Grid.Item>
                <Grid.Item>
                  {getLocalizedText("payment_order.wire_reference.info", {
                    value: paymentOrder.wireReference,
                  })}
                </Grid.Item>
              </Grid>
            </Stack.Item>
          </>
        )}

      {(paymentOrder.payoutReference || paymentOrder.payoutReferenceCustom) && (
        <>
          <Spacer weight={ESpacerWeight.W08} />
          <Stack.Item>
            <Grid spacing="extraTight" alignItems="center">
              <Grid.Item>
                <Icon source="billStack" size="small" />
              </Grid.Item>
              <Grid.Item>
                {getLocalizedText("payment_order.reference.info", {
                  value:
                    paymentOrder.payoutReference ||
                    paymentOrder.payoutReferenceCustom ||
                    "",
                })}
              </Grid.Item>
            </Grid>
          </Stack.Item>
        </>
      )}

      {paymentOrder.repetitionType !== EPaymentRepetitionType.Once &&
        !paymentOrder.isIndexable && (
          <>
            <Spacer weight={ESpacerWeight.W08} />
            <Stack.Item>
              <Grid spacing="extraTight" alignItems="center">
                <Grid.Item>
                  <Icon source={"billStack"} size="small" />
                </Grid.Item>
                <Grid.Item>
                  {getLocalizedText("payment_order.not_indexable.info")}
                </Grid.Item>
              </Grid>
            </Stack.Item>
          </>
        )}

      {contract?.rentDiscountEnabled && (
        <>
          <Spacer weight={ESpacerWeight.W08} />
          <Stack.Item>
            <Grid spacing="extraTight" alignItems="center">
              <Grid.Item>
                <Icon source="billStack" size="small" />
              </Grid.Item>
              <Grid.Item>
                {getLocalizedText(
                  "payment.overview.payment_order.rent_discount_info_amount",
                  {
                    amount: `${formatCurrency(
                      contract?.rentDiscountAmount || 0,
                    )}`,
                  },
                )}
              </Grid.Item>
            </Grid>
          </Stack.Item>

          <Spacer weight={ESpacerWeight.W08} />
          <Stack.Item>
            <Grid spacing="extraTight" alignItems="center">
              <Grid.Item>
                <Icon source="billStack" size="small" />
              </Grid.Item>
              <Grid.Item>
                {getLocalizedText(
                  "payment.overview.payment_order.rent_discount_info_offset",
                  {
                    offset: `${contract?.rentDiscountOffset || 0}`,
                  },
                  contract?.rentDiscountOffset || 0,
                )}
              </Grid.Item>
            </Grid>
          </Stack.Item>
        </>
      )}

      {period && (
        <>
          <Spacer weight={ESpacerWeight.W08} />
          <Stack.Item>
            <Grid spacing="extraTight" alignItems="center">
              <Grid.Item>
                <Icon source="calendar" size="small" />
              </Grid.Item>
              <Grid.Item>{period}</Grid.Item>
            </Grid>
          </Stack.Item>
        </>
      )}

      {paymentOrder.repetitionType === EPaymentRepetitionType.Once && (
        <>
          <Spacer weight={ESpacerWeight.W08} />
          <Stack.Item>
            <Grid spacing="extraTight" alignItems="center">
              <Grid.Item>
                <Icon source="calendar" size="small" />
              </Grid.Item>
              <Grid.Item>
                {getLocalizedText(
                  "payment.overview.payment_order_frequency_meta.once",
                  {
                    dueDate: paymentOrder.dueDateAt
                      ? utils.date.format(paymentOrder.dueDateAt, "d MMMM yyyy")
                      : "",
                  },
                )}
              </Grid.Item>
            </Grid>
          </Stack.Item>
        </>
      )}

      {paymentOrder.repetitionType !== EPaymentRepetitionType.Once &&
        paymentOrder.nextInvitationAt &&
        paymentOrder.nextContractualPayDate && (
          <>
            <Spacer weight={ESpacerWeight.W08} />
            <Stack.Item>
              <Grid spacing="extraTight" alignItems="center">
                <Grid.Item>
                  <Icon source="calendar" size="small" />
                </Grid.Item>
                <Grid.Item>
                  {getLocalizedText(
                    "payment_order.next_date_info.contractual_date",
                    {
                      nextContractualPayDate: formatDate(
                        paymentOrder.nextContractualPayDate,
                        "d MMMM yyyy",
                      ),
                    },
                  )}
                </Grid.Item>
              </Grid>
            </Stack.Item>

            <Spacer weight={ESpacerWeight.W08} />
            <Stack.Item>
              <Grid spacing="extraTight" alignItems="center">
                <Grid.Item>
                  <Icon source="calendar" size="small" />
                </Grid.Item>
                <Grid.Item>
                  {getLocalizedText(
                    "payment_order.next_date_info.invitation_date",
                    {
                      nextInvitationAt: formatDate(
                        paymentOrder.nextInvitationAt,
                        "d MMMM yyyy",
                      ),
                    },
                  )}
                </Grid.Item>
              </Grid>
            </Stack.Item>
          </>
        )}

      {hasBookkeeping && paymentOrder.bookkeepingGeneralLedgerAccount && (
        <>
          <Spacer weight={ESpacerWeight.W08} />
          <Stack.Item>
            <Grid spacing="extraTight" alignItems="center">
              <Grid.Item>
                <Icon source="layout" size="small" />
              </Grid.Item>
              <Grid.Item>
                {getLocalizedText(
                  "payment.payment_order.bookkeeping.general_ledger_account",
                )}
                {": "}
                {paymentOrder.bookkeepingGeneralLedgerAccount}
              </Grid.Item>
            </Grid>
            {paymentOrder.bookkeepingCostCenter && (
              <Grid spacing="extraTight" alignItems="center">
                <Grid.Item>
                  <Icon source="layout" size="small" />
                </Grid.Item>
                <Grid.Item>
                  {getLocalizedText(
                    "payment.payment_order.bookkeeping.cost_center",
                  )}
                  {": "}
                  {paymentOrder.bookkeepingCostCenter}
                </Grid.Item>
              </Grid>
            )}
            {paymentOrder.bookkeepingCostUnit && (
              <Grid spacing="extraTight" alignItems="center">
                <Grid.Item>
                  <Icon source="layout" size="small" />
                </Grid.Item>
                <Grid.Item>
                  {getLocalizedText(
                    "payment.payment_order.bookkeeping.cost_unit",
                  )}
                  {": "}
                  {paymentOrder.bookkeepingCostUnit}
                </Grid.Item>
              </Grid>
            )}
          </Stack.Item>
        </>
      )}
    </div>
  );

  const handleInfoTabClick = () => {
    setTab(EPaymentOrderTabs.Info);
  };

  const handleHistoryTabClick = () => {
    setTab(EPaymentOrderTabs.History);
  };

  const handleEdit = (paymentOrder: IPaymentOrder) => () => {
    navigate(
      `/properties/${paymentOrder.propertyId}/payments-v2/${paymentOrder.id}/edit`,
    );
  };

  const handleRemoveWithConfirm = (paymentOrderId: number) => () => {
    const handleRemove = (paymentOrderId: number) => () => {
      dispatch(
        paymentActions.deletePaymentOrder.actions.start({ paymentOrderId }),
      );
    };

    confirm({
      title: getLocalizedText("system.delete.confirm", {
        typePrefix: getLocalizedText("system.model.payment_order.prefix"),
        type: getLocalizedText("system.model.payment_order").toLowerCase(),
      }),
      primaryActions: [
        {
          title: getLocalizedText("system.remove"),
          onPress: handleRemove(paymentOrderId),
        },
      ],
    });
  };

  let tabs = [
    {
      name: "dashboard",
      permission: "propertyDashboard:visit",
      content: getLocalizedText("payment_order.detail.tab.info"),
      onClick: handleInfoTabClick,
      isActive: tab === EPaymentOrderTabs.Info,
    },
    {
      name: "payments",
      permission: "propertyPayments:visit",
      content: getLocalizedText("payment_order.detail.tab.history"),
      onClick: handleHistoryTabClick,
      isActive: tab === EPaymentOrderTabs.History,
    },
  ];

  const dropdown = [];

  if (
    !paymentOrder.completedAt &&
    paymentOrder.isEditable &&
    paymentOrder.roles.includes(EPaymentOrderRole.Write) &&
    !paymentOrder.deletedAt
  ) {
    dropdown.push({
      content: getLocalizedText("payments.overview.payment_order.edit.action"),
      onClick: handleEdit(paymentOrder),
    });

    dropdown.push({
      content: (
        <TextStyle variation={"negative"}>
          {getLocalizedText("system.remove")}
        </TextStyle>
      ),
      onClick: handleRemoveWithConfirm(paymentOrder.id),
    });
  }

  const renderInfo = () => (
    <PaymentOrderInfo paymentOrder={paymentOrder} setTab={setTab} />
  );
  const renderHistory = () => (
    <PaymentOrderHistory paymentOrder={paymentOrder} setTab={setTab} />
  );

  return (
    <Page
      title={`${getTitle(paymentOrder)} ${formatCurrency(amounts.amount)}`}
      metadata={metadata}
      tabs={tabs}
      dropdown={dropdown.length > 0 ? (dropdown as IAction[]) : undefined}
      setDocumentTitle={false}
    >
      {tab === EPaymentOrderTabs.Info && renderInfo()}
      {tab === EPaymentOrderTabs.History && renderHistory()}
    </Page>
  );
};
